/**
 * @author NikolaV
 */

import './ImageUploader.scss';
import { IGeolocationResult } from '@openlayers/geolocation/GeolocationLayer';
import { LStorage } from '@shared/Storage';
import { UppyWrapper } from '@shared/files/UppyWrapper';

// libs
import * as React from 'react';
import { observer } from 'mobx-react';
import { computed, makeObservable, observable } from 'mobx';
import { transform as olTransform } from 'ol/proj';
import { FiletreeSelectReact } from '@shared/ui-react/FiletreeSelectReact';
import { ProjectPropertyJsonArrayStore } from '@pages/project-settings/shared/ProjectPropertyJsonArrayStore';
import { ImagesService } from '@shared/images/ImagesService';
import { cs } from '@shared/utils/utils-classname';
import { FileTreeService } from '@shared/services/FileTreeService';
import exifr from 'exifr';
import { PropertiesFileFillComponent } from '@pages/files/dialogs/PropertiesFillDialog';
import { DocumentPropertiesService } from '@shared/services/DocumentPropertiesService';
import { Button } from '@shared/ui-react/Button';
import { UppyFile } from '@uppy/core';
import { FileServiceClient } from '@shared/files/FileServiceClient';

@observer
export class ImageUploader extends React.Component<{
    initialFiles?: File[];
    treeData: ITreeData;
    folderId?: string;
    onQueueComplete?: () => void;
    onClickDone?: () => void;
    getGeolocation?: () => IGeolocationResult;
    capture?: boolean; // http://anssiko.github.io/html-media-capture/
    persistState?: boolean;
    canRenameImage?: boolean;
    getAlignment?: () => { alignmentName: string; station: number };
    getActiveModelName?: () => string;
    getBValue?: () => string;
}> {
    stateStorageKey = kuura.getProjectId() + '_image-uploader-state';
    nameTemplatesStore = new ProjectPropertyJsonArrayStore<INamingTemplate>(ImagesService.nameTemplatesStorageKey);
    exifCache = new Map<
        File,
        { DateTimeOriginal: string; CreateDate: string; latitude: number; longitude: number; direction: number; ProjectionType: string; Make: string; Model: string }
    >();
    propertiesFileFillComponent: PropertiesFileFillComponent;
    private canFillProperties = kuura.hasPrivilege('Edit_Document');

    @observable description = ``;
    @observable direction = -1;
    @observable activeNameTemplateIndex = 0;
    @observable isLoading = false;
    @observable showProperties = false;
    @observable arePropertiesValid = null;
    @observable arePropertiesInitialized = false;
    treeData: ITreeData = this.props.treeData;

    @observable folderId = this.props.folderId || this.props.treeData.id;

    @computed get folderUuid(): string {
        const node = FileTreeService.findNodeById(this.treeData, this.folderId, true);
        return node ? node.uuid : null;
    }

    @computed get folderName(): string {
        const node = FileTreeService.findNodeById(this.treeData, this.folderId, true);
        return node ? node.name : null;
    }

    constructor(props) {
        super(props);

        makeObservable(this);

        if (this.props.persistState) {
            // load saved state
            const state = {
                folderId: this.props.treeData.id,
                description: '',
                direction: -1,
                ...LStorage.getOrDefault(this.stateStorageKey, {}),
            };
            Object.assign(this, state);
        }
    }

    componentDidMount() {
        if (this.props.canRenameImage) {
            this.isLoading = true;
            this.nameTemplatesStore.fetch().always(() => {
                if (this.nameTemplatesStore.allItems.length === 0) {
                    this.nameTemplatesStore.allItems.push({
                        // per spec, have a default template when no templates exist
                        t: ImagesService.defaultNameTemplate,
                    });
                }
                this.isLoading = false;
            });
        }
    }

    componentWillUnmount() {
        this.exifCache.clear();
        this.persistState();
    }

    onFillInitialized = () => (this.arePropertiesInitialized = true);
    onFillValidationStatusChanged = status => (this.arePropertiesValid = status);

    render() {
        const propertiesNotValid = this.canFillProperties && (!this.arePropertiesValid || !this.arePropertiesInitialized);

        return (
            <UppyWrapper
                onlyImages={true}
                initialFiles={this.props.initialFiles}
                onClickDone={this.props.onClickDone}
                onAddedFile={this.handleOnAdded}
                onQueueComplete={this.props.onQueueComplete}
                getS3Url={this.getS3Url}
                postProcess={null}
                cantUploadMsg={this.arePropertiesInitialized && !this.arePropertiesValid ? __('js_properties.fill_required_before_upload') : null}
                getPhotoMetadata={file => this.exifCache.get(file.data as File) as any}
                disabled={propertiesNotValid}
            >
                <>
                    <div
                        className={cs('padded flex-column photo-upload-properties-wrapper', { open: this.showProperties })}
                        style={{ position: 'absolute', top: -2, bottom: -2, left: -2, right: -2, zIndex: 9999 }}
                    >
                        <Button primary icon='iki iki-arrow-left' onClick={() => (this.showProperties = false)} style={{ marginBottom: '1.5em' }} />
                        <div style={{ marginBottom: 20 }}>
                            {__('js_properties.properties_assigned_to_folder_x')}:<br />
                            <strong>{this.folderName}</strong>
                        </div>
                        <div className='of-a'>
                            <PropertiesFileFillComponent
                                ref={el => (this.propertiesFileFillComponent = el)}
                                folderUuid={this.folderUuid}
                                onInitialized={this.onFillInitialized}
                                onValidationStatusChanged={this.onFillValidationStatusChanged}
                            />
                        </div>
                    </div>

                    <div className={'photo-upload-wrapper card padded of-a'} style={{ maxWidth: 550 }}>
                        {this.props.canRenameImage && (
                            <>
                                <div className={cs({ 'overlay-loading': this.isLoading })}>
                                    <label className='styled'>{__('js_name_templates.photo_name_template')}</label>
                                    <select value={this.activeNameTemplateIndex} onChange={e => (this.activeNameTemplateIndex = parseInt(e.currentTarget.value))}>
                                        <option value={-1}>{__('js_uploadimage.keep_original_filename')}</option>
                                        {this.nameTemplatesStore.allItems.map((item, i) => (
                                            <option value={i} key={i}>
                                                {item.n?.length > 0 ? item.n : item.t}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </>
                        )}
                        <div style={{ marginTop: 16 }}>
                            <label className='styled'>
                                {kuura.getMessage('general.description')}
                                <br />
                                <div className='flex-row'>
                                    <input type='text' value={this.description} onChange={e => (this.description = e.currentTarget.value)} />
                                    <i className='iki iki-cross close' onClick={() => (this.description = '')}></i>
                                </div>
                            </label>
                        </div>
                        <div className='flex-row gap-half of-h' style={{ marginTop: 10, flexShrink: 0 }}>
                            <div className='of-h' style={{ flexGrow: 1 }}>
                                <label className='styled'>{kuura.getMessage('general.folder')}</label>
                                <FiletreeSelectReact
                                    formFieldName='folderId'
                                    selectable='folder'
                                    treeData={this.props.treeData}
                                    selectedId={this.folderId}
                                    onChange={val => this.setFolderId(val)}
                                    storageKey={this.stateStorageKey + '_folderSelectTree'}
                                />
                            </div>
                            {this.canFillProperties && (
                                <div>
                                    <label className='styled'>{__('js_properties.properties')}</label>
                                    <Button onClick={() => (this.showProperties = !this.showProperties)}>{__('js_files.edit_properties')}</Button>
                                </div>
                            )}
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <label className='styled'>
                                {kuura.getMessage('tablet.direction')}
                                <br />
                            </label>
                            <div className='flex-row'>
                                {utils.isIE ? null : (
                                    <input type='range' min={-1} max={360} name='direction' value={this.direction} onChange={this.handleAngleChange} required={true} />
                                )}
                                <input
                                    type='number'
                                    min={-1}
                                    max={360}
                                    value={this.direction}
                                    onChange={this.handleAngleChange}
                                    required={true}
                                    style={{ width: 70, marginLeft: 10 }}
                                />
                                <span className='image-direction-arrow' style={{ transform: 'rotate(' + this.direction + 'deg)', verticalAlign: 'top' }}></span>
                            </div>
                        </div>
                    </div>
                </>
            </UppyWrapper>
        );
    }

    getS3Url = (file: UppyFile) => {
        this.persistState();

        let filename = file.name;
        let lat = null;
        let lon = null;
        let altitude = null;
        let direction = this.direction;
        let baseProperties: Record<string, string> = {};

        let originalDate = null;
        if (this.exifCache.has(file.data as File)) {
            try {
                const exif = this.exifCache.get(file.data as File);
                if (exif) {
                    const mm = moment(exif.DateTimeOriginal ?? exif.CreateDate);
                    originalDate = mm.isValid() ? mm : null;
                    lat = exif.latitude;
                    lon = exif.longitude;
                    if (exif.direction != null) {
                        direction = exif.direction;
                    }

                    if (exif.ProjectionType) {
                        baseProperties.projection = exif.ProjectionType;
                    }
                    if (exif.Make) {
                        baseProperties.make = exif.Make;
                    }
                    if (exif.Model) {
                        baseProperties.model = exif.Model;
                    }
                }
            } catch {}
        }

        if (!lat && !lon && this.props.getGeolocation) {
            const gps = this.props.getGeolocation();
            if (gps && gps.position) {
                const [e, n] = gps.position;
                const coord = olTransform([e, n], 'EPSG:3857', 'EPSG:4326');
                lat = coord[1];
                lon = coord[0];
                if (typeof gps.altitude === 'number') {
                    altitude = gps.altitude;
                }
            } else {
                console.log('No geolocation data to upload with image');
            }
        }

        if (this.activeNameTemplateIndex > -1 && this.props.canRenameImage) {
            const folderNode = FileTreeService.findNodeById(this.props.treeData, this.folderId, true);
            filename = this.renameImage(this.nameTemplatesStore.allItems[this.activeNameTemplateIndex]?.t, file.name, folderNode?.name, this.description, originalDate, [lat, lon]);
        }

        return FileServiceClient.getS3UploadPath(file.data as File, {
            fileType: 'image',
            folderUuid: this.folderUuid,
            contentType: file.type, // take type from UppyFile, the one in File can be empty
            filename,
            description: (file.meta?.description as string) ?? this.description,
            originalTimestamp: originalDate ? originalDate.toISOString() : null,
            direction,
            lat,
            lon,
            altitude,
            properties: this.getPropertiesForSaving(),
        });
    };

    getPropertiesForSaving = () => {
        let fillValues = this.propertiesFileFillComponent.fillComponent.fillValues;
        if (Object.values(fillValues).filter(v => v != null).length === 0) {
            return null;
        }
        const arr = DocumentPropertiesService.sanitizeFillValuesBeforeSaving(
            this.propertiesFileFillComponent.fillComponent.fillValues || {},
            Object.values(this.propertiesFileFillComponent.fillComponent.properties).map(p => p.propertyDeclaration)
        );

        return arr.reduce((obj, curr) => {
            return {
                ...obj,
                [curr.key]: curr.value,
            };
        }, {});
    };

    handleAngleChange = e => (this.direction = e.currentTarget.value);

    handleOnAdded = async (file: UppyFile) => {
        try {
            const pickExif = [
                'DateTimeOriginal',
                'CreateDate',
                'latitude',
                'longitude',
                'GPSLatitude',
                'GPSLongitude',
                'GPSImgDirection',
                'GPSDestBearing',
                'GPSAltitude',
                'Make',
                'Model',
            ];
            const pickXMP = [
                'ProjectionType',
                'FlightYawDegree',
                'FlightPitchDegree',
                'FlightRollDegree',
                'GimbalYawDegree',
                'GimbalPitchDegree',
                'GimbalRollDegree',
                'GimbalReverse',
            ];

            let output = await exifr.parse(file.data, {
                exif: {
                    pick: pickExif,
                },
                xmp: {
                    pick: pickXMP,
                },
            });
            console.log('added photo metadata:', output);

            let exifDirection =
                (output.GPSImgDirection == 0 ? null : output.GPSImgDirection) ??
                (output.GPSDestBearing == 0 ? null : output.GPSDestBearing) ??
                output.FlightYawDegree ??
                output.GimbalYawDegree ??
                0;

            exifDirection = ((exifDirection % 360) + 360) % 360; // normalize to 0-360
            console.log('added photo computed direction:', exifDirection);
            output.direction = exifDirection;

            if (output && Object.keys(output).length > 0) {
                this.exifCache.set(file.data as File, output);
            }
        } catch {}
    };

    persistState = () => {
        if (this.props.persistState) {
            LStorage.setItem(this.stateStorageKey, {
                folderId: this.folderId,
                description: this.description,
                direction: this.direction,
            });
        }
    };

    setFolderId = val => {
        this.folderId = val;
    };

    renameImage(preset: string, originalName: string, folderName: string, description: string, originalDateM: any, latLon: [number, number]): string {
        const alignment = typeof this.props.getAlignment === 'function' ? this.props.getAlignment() : { alignmentName: null, station: null };
        const model = typeof this.props.getActiveModelName === 'function' ? this.props.getActiveModelName() : null;
        const bValue = typeof this.props.getBValue === 'function' ? this.props.getBValue() : null;

        return ImagesService.renameImage(preset, originalName, folderName, description, originalDateM?.toDate(), latLon, model, bValue, alignment, null);
    }
}
